import { getSession } from './auth';
import { S3Client, PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';

const REGION = import.meta.env.VITE_COGNITO_REGION;
const BUCKET_REGION = import.meta.env.VITE_AWS_REGION ?? REGION;
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const IDENTITY_POOL_ID = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID;
const BUCKET = import.meta.env.VITE_ROUTE_USERDATA;

/**
 * Decode a JWT payload segment, handling URL-safe base64.
 */
function decodeJwtPayload(token) {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (base64.length % 4)) % 4;
  const paddedBase64 = base64 + '='.repeat(padding);
  return JSON.parse(atob(paddedBase64));
}

/**
 * Return the first Cognito group the user belongs to, or null if none.
 */
export function getGroupFromSession(session) {
  const payload = decodeJwtPayload(session.getIdToken().getJwtToken());
  const groups = payload['cognito:groups'];
  return Array.isArray(groups) && groups.length > 0 ? groups[0] : null;
}

/**
 * Build an S3Client authenticated via the Cognito Identity Pool,
 * using the authenticated user's ID token as a Cognito login.
 */
function makeS3Client(session) {
  const idToken = session.getIdToken().getJwtToken();
  const logins = {
    [`cognito-idp.${REGION}.amazonaws.com/${USER_POOL_ID}`]: idToken,
  };
  return new S3Client({
    region: BUCKET_REGION,
    credentials: fromCognitoIdentityPool({
      identityPoolId: IDENTITY_POOL_ID,
      logins,
      clientConfig: { region: REGION },
    }),
  });
}

/**
 * Upload a routes.json string for the authenticated user's group.
 *
 * Bucket key: groups/{group}/routes.json
 *
 * @param {string} json  Serialised JSON string
 * @returns {Promise<string>} The S3 key that was written
 */
export async function uploadRoutesJson(json) {
  const session = await getSession();
  const group = getGroupFromSession(session);
  if (!group) throw new Error('User does not belong to any group');

  const key = `groups/${group}/routes.json`;
  await makeS3Client(session).send(
    new PutObjectCommand({
      Bucket: BUCKET,
      Key: key,
      Body: json,
      ContentType: 'application/json',
    }),
  );
  return key;
}

/**
 * Upload a single image File for the authenticated user's group.
 *
 * Bucket key: groups/{group}/images/{file.name}
 *
 * @param {File} file
 * @returns {Promise<string>} The S3 key that was written
 */
export async function uploadImage(file) {
  const session = await getSession();
  const group = getGroupFromSession(session);
  if (!group) throw new Error('User does not belong to any group');

  const key = `groups/${group}/images/${file.name}`;
  const buffer = await file.arrayBuffer();
  await makeS3Client(session).send(
    new PutObjectCommand({
      Bucket: BUCKET,
      Key: key,
      Body: new Uint8Array(buffer),
      ContentType: file.type || 'application/octet-stream',
    }),
  );
  return key;
}

/**
 * Upload multiple image Files for the authenticated user's group.
 *
 * @param {FileList|File[]} files
 * @param {(done: number, total: number) => void} [onProgress]
 * @returns {Promise<string[]>} Array of S3 keys written
 */
export async function uploadImages(files, onProgress) {
  const fileArray = Array.from(files);
  const keys = [];
  for (let i = 0; i < fileArray.length; i++) {
    keys.push(await uploadImage(fileArray[i]));
    onProgress?.(i + 1, fileArray.length);
  }
  return keys;
}

/**
 * Fetch and parse the routes.json for the authenticated user's group.
 *
 * @returns {Promise<{ routes: Array, group: string }>}
 */
export async function fetchUserRoutes() {
  const session = await getSession();
  const group = getGroupFromSession(session);
  if (!group) throw new Error('User does not belong to any group');

  const key = `groups/${group}/routes.json`;
  const response = await makeS3Client(session).send(
    new GetObjectCommand({ Bucket: BUCKET, Key: key }),
  );
  const text = await response.Body.transformToString();
  const data = JSON.parse(text);
  const routes = Array.isArray(data)
    ? data
    : (data && Array.isArray(data.routes) ? data.routes : null);

  if (!routes) {
    throw new Error('Invalid routes.json format: expected an array or an object with a routes array');
  }

  return { routes, group };
}

/**
 * Return a pre-signed URL for an image stored in the user's group prefix.
 * The URL is valid for 1 hour.
 *
 * @param {string} group
 * @param {string} filename  bare filename, e.g. "photo.jpg"
 * @returns {Promise<string>}
 */
export async function getSignedImageUrl(group, filename) {
  const session = await getSession();
  const name = filename.split('/').pop();
  const key = `groups/${group}/images/${name}`;
  const command = new GetObjectCommand({ Bucket: BUCKET, Key: key });
  return getSignedUrl(makeS3Client(session), command, { expiresIn: 3600 });
}
